import React, { useRef } from 'react';
import PropTypes from 'prop-types';

const LONG_PRESS_MS = 500;
const MOVE_TOLERANCE_PX = 8;

const initials = (name) => {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0] || '')
    .join('')
    .toUpperCase();
};

// Lineup items come from V3OfferBlueprint's `:lineup` field — a slim hash
// with `performer_name` / `performer_avatar_url` / `act_type` already
// flattened (no nested `primary_performer` association). The nested form
// only exists on the *active* offer in the page context.
const performerName = (item) =>
  item?.performer_name
  || item?.primary_performer?.artistable?.name
  || 'Unnamed';

const isUnassigned = (item) => !item?.performer_name && !item?.primary_performer;

const V3MobileLineupStrip = ({
  lineup,
  activeOfferId,
  selectingOfferId,
  onSelect,
  onAddArtist,
  onLongPressChip,
}) => {
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const pointerStartRef = useRef({ x: 0, y: 0 });

  const clearTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handlePointerDown = (e) => {
    longPressFiredRef.current = false;
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    clearTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      if (onLongPressChip) onLongPressChip();
    }, LONG_PRESS_MS);
  };

  const handlePointerMove = (e) => {
    const dx = Math.abs(e.clientX - pointerStartRef.current.x);
    const dy = Math.abs(e.clientY - pointerStartRef.current.y);
    if (dx > MOVE_TOLERANCE_PX || dy > MOVE_TOLERANCE_PX) clearTimer();
  };

  const handlePointerEnd = () => clearTimer();

  const handleChipClick = (chipOfferId) => () => {
    // If the long-press fired, the chip-press already opened the reorder
    // sheet — don't also swap the active offer.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (chipOfferId === activeOfferId) return;
    onSelect(chipOfferId);
  };

  return (
    <div className="lineup-strip" role="tablist" aria-label="Artists on this bill" data-testid="lineup-strip">
      {lineup.map((item) => {
        const isActive = item.id === activeOfferId;
        const isEmpty = isUnassigned(item);
        const isLoading = item.id === selectingOfferId;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-loading={isLoading ? 'true' : 'false'}
            data-testid="lineup-strip-chip"
            className={[
              'lineup-strip__chip',
              isActive && 'lineup-strip__chip--active',
              isEmpty && 'lineup-strip__chip--empty',
            ].filter(Boolean).join(' ')}
            onClick={handleChipClick(item.id)}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            onPointerLeave={handlePointerEnd}
          >
            <span className="lineup-strip__avatar" aria-hidden="true">
              {isEmpty
                ? <i className="far fa-plus" />
                : initials(performerName(item))}
            </span>
            <span>{isEmpty ? 'Add artist' : performerName(item)}</span>
          </button>
        );
      })}

      <button
        type="button"
        className="lineup-strip__add"
        aria-label="Add artist"
        data-testid="lineup-strip-add"
        onClick={onAddArtist}
      >
        <i className="far fa-plus" aria-hidden="true" />
      </button>
    </div>
  );
};

V3MobileLineupStrip.propTypes = {
  lineup: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeOfferId: PropTypes.number,
  selectingOfferId: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onAddArtist: PropTypes.func.isRequired,
  onLongPressChip: PropTypes.func,
};

V3MobileLineupStrip.defaultProps = {
  activeOfferId: null,
  selectingOfferId: null,
  onLongPressChip: null,
};

export default V3MobileLineupStrip;
